/**
 * Voice-call sound effects — synthesized via the Web Audio API so no binary
 * assets ship with the bundle (same approach as `utils/chat/sounds.ts`).
 *
 *   startRingtone() / stopRingtone()  — incoming call: looping two-tone bell
 *   startRingback() / stopRingback()  — outgoing call: looping "calling" purr
 *   playConnectTone()                 — short rising blip when the call connects
 *   playEndTone()                     — short descending blip when it ends
 *   setSystemRingtone(base64)         — swap the incoming bell for the OS
 *                                       ringtone (desktop app); synth = fallback
 *
 * The looping effects (ringtone/ringback) schedule one burst, then repeat on a
 * timer until stopped, so they're driven straight from the call state machine.
 *
 * The AudioContext is created lazily to dodge the autoplay-policy warning before
 * any user gesture, and resumed transparently if the browser suspended it.
 * Playback is best-effort: a browser that keeps the context suspended (no prior
 * gesture) simply stays silent — the animated call UI is the guaranteed signal.
 * Every failure path is silent; a missing sound must never block a call.
 */

/**
 * ALL mutable audio state lives in a single object hung off `window`, NOT in
 * module-level `let`s. Why: with React Fast Refresh (dev), editing this file
 * hot-swaps the module — a fresh instance gets `ringtoneTimer = null` while the
 * OLD instance's `setInterval` keeps firing, orphaned and unreachable from the
 * new `stopRingtone()`. That orphan rings forever regardless of call state.
 * A window-scoped singleton means every module instance shares the same timer,
 * AudioContext and active-flags, so `stop*()` is always authoritative and the
 * start-guard can never spawn a duplicate loop. Harmless in production (one
 * instance). See [[project_voice_call_premium]].
 */
interface CallAudioState {
  ctx: AudioContext | null;
  // All ring/ringback bursts route through `ringGain`; one-shot tones route
  // through `toneGain`. Dropping `ringGain` to 0 silences EVERY scheduled ring
  // oscillator instantly (Web Audio can't unschedule a started oscillator, so
  // clearing the interval alone leaves a ~0.7s audible tail). Separate gains so
  // killing the ring never mutes the connect/end one-shots.
  ringGain: GainNode | null;
  toneGain: GainNode | null;
  ringtoneTimer: ReturnType<typeof setInterval> | null;
  ringbackTimer: ReturnType<typeof setInterval> | null;
  ringtoneActive: boolean;
  ringbackActive: boolean;
  // OS ringtone (desktop app): raw base64 audio handed in by setSystemRingtone,
  // its decoded buffer (lazy, reused across rings), a sticky decode-failure
  // flag (bad bytes won't decode better next ring — fall back to the synth),
  // and the live looping source while an incoming call rings. The source
  // routes through `ringGain`, so the instant-silence guarantee above applies
  // to it exactly as to the synthesized bursts.
  systemRingtone: string | null;
  systemRingtoneBuffer: AudioBuffer | null;
  systemRingtoneFailed: boolean;
  ringtoneSource: AudioBufferSourceNode | null;
}

function S(): CallAudioState {
  const w = window as any;
  if (!w.__messengerCallAudio) {
    w.__messengerCallAudio = {
      ctx: null,
      ringGain: null,
      toneGain: null,
      ringtoneTimer: null,
      ringbackTimer: null,
      ringtoneActive: false,
      ringbackActive: false,
      systemRingtone: null,
      systemRingtoneBuffer: null,
      systemRingtoneFailed: false,
      ringtoneSource: null,
    } as CallAudioState;
  }
  return w.__messengerCallAudio;
}

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const s = S();
  if (s.ctx) return s.ctx;
  const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  try {
    s.ctx = new Ctor();
    s.ringGain = s.ctx!.createGain();
    s.ringGain.gain.value = 1;
    s.ringGain.connect(s.ctx!.destination);
    s.toneGain = s.ctx!.createGain();
    s.toneGain.gain.value = 1;
    s.toneGain.connect(s.ctx!.destination);
    return s.ctx;
  } catch {
    return null;
  }
}

/** Instantly silence (gain→0) or re-open (gain→1) the ring bus. */
function setRingBus(open: boolean): void {
  const s = S();
  if (!s.ctx || !s.ringGain) return;
  try {
    const now = s.ctx.currentTime;
    s.ringGain.gain.cancelScheduledValues(now);
    s.ringGain.gain.setValueAtTime(open ? 1 : 0, now);
  } catch {
    /* setValueAtTime unsupported / context closed — non-fatal */
  }
}

async function ensureRunning(ctx: AudioContext): Promise<boolean> {
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }
  return ctx.state === 'running';
}

type Tone = {
  /** Hz */
  frequency: number;
  /** Seconds from the burst start */
  startOffset: number;
  /** Seconds */
  duration: number;
  /** Linear gain peak (0..1) */
  gain: number;
  type?: OscillatorType;
};

/**
 * Play a one-shot cluster of tones with soft attack/release (no clicks).
 *
 * `isActive` guards the autoplay-resume race: while the AudioContext is
 * suspended (no user gesture yet — the incoming-call case) a burst parks on
 * `ensureRunning`'s `resume()`. If the loop is stopped meanwhile, the burst
 * must NOT emit when the gesture finally resumes the context — otherwise every
 * queued ring fires the moment you press Accept. We re-check `isActive()` after
 * the await so `stop*()` is authoritative over in-flight bursts.
 */
async function playBurst(
  tones: Tone[],
  isActive?: () => boolean,
  bus: 'ring' | 'tone' = 'tone',
): Promise<void> {
  const ctx = getContext();
  if (!ctx) return;
  if (!(await ensureRunning(ctx))) return;
  if (isActive && !isActive()) return;
  const s = S();
  const dest: AudioNode = (bus === 'ring' ? s.ringGain : s.toneGain) ?? ctx.destination;
  const t0 = ctx.currentTime;
  for (const t of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = t.type ?? 'sine';
    osc.frequency.value = t.frequency;
    gain.gain.setValueAtTime(0, t0 + t.startOffset);
    gain.gain.linearRampToValueAtTime(t.gain, t0 + t.startOffset + 0.02);
    gain.gain.setValueAtTime(t.gain, t0 + t.startOffset + t.duration - 0.04);
    gain.gain.linearRampToValueAtTime(0, t0 + t.startOffset + t.duration);
    osc.connect(gain).connect(dest);
    osc.start(t0 + t.startOffset);
    osc.stop(t0 + t.startOffset + t.duration + 0.02);
  }
}

// ── Looping effects ────────────────────────────────────────────────────────
// Each loop keeps its repeat timer here so it can be cleanly stopped. The
// in-flight oscillators are short and fade out on their own.

/** Incoming-call bell: a bright double "ring-ring", repeating every 2.6 s. */
function ringtoneBurst(): void {
  if (!S().ringtoneActive) return;
  // Two quick rings (a "ring-ring"), each a warm two-tone chord.
  void playBurst([
    { frequency: 660, startOffset: 0.0, duration: 0.32, gain: 0.16, type: 'triangle' },
    { frequency: 880, startOffset: 0.0, duration: 0.32, gain: 0.10, type: 'sine' },
    { frequency: 660, startOffset: 0.42, duration: 0.32, gain: 0.16, type: 'triangle' },
    { frequency: 880, startOffset: 0.42, duration: 0.32, gain: 0.10, type: 'sine' },
  ], () => S().ringtoneActive, 'ring');
}

/**
 * Provide the OS ringtone (base64-encoded audio file, e.g. macOS "Reflection"
 * or a Windows Ring0x.wav) to play for incoming calls instead of the
 * synthesized bell. Decoded lazily on the first ring so the AudioContext
 * stays untouched until a call actually arrives. Pass null to keep the synth.
 */
export function setSystemRingtone(base64: string | null): void {
  const s = S();
  s.systemRingtone = base64 || null;
  s.systemRingtoneBuffer = null;
  s.systemRingtoneFailed = false;
  // Swapping tones mid-ring: kill the live loop so the old tone can't keep
  // playing under whatever the next tick starts (the tick loop, if active,
  // brings up the new sound on its own).
  if (s.ringtoneSource) {
    try { s.ringtoneSource.stop(); } catch { /* already stopped */ }
    try { s.ringtoneSource.disconnect(); } catch { /* noop */ }
    s.ringtoneSource = null;
  }
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Loop the system ringtone through the ring bus. Async (decode + autoplay
 * resume), so `ringtoneActive` is re-checked after every await — a stop
 * issued meanwhile must win, same race playBurst guards against. Returns
 * false only when this path can't ever produce sound (no bytes / bad decode)
 * so the tick falls back to the synthesized bell; a context that is merely
 * still suspended returns true and the next tick retries.
 */
async function playSystemRingtone(): Promise<boolean> {
  const s = S();
  if (!s.systemRingtone || s.systemRingtoneFailed) return false;
  const ctx = getContext();
  if (!ctx) return false;
  if (!s.systemRingtoneBuffer) {
    try {
      s.systemRingtoneBuffer = await ctx.decodeAudioData(base64ToArrayBuffer(s.systemRingtone));
    } catch {
      s.systemRingtoneFailed = true;
      return false;
    }
  }
  if (!(await ensureRunning(ctx))) return true; // no gesture yet — retry next tick
  if (!s.ringtoneActive || s.ringtoneSource) return true; // stopped meanwhile / already looping
  try {
    const src = ctx.createBufferSource();
    src.buffer = s.systemRingtoneBuffer;
    src.loop = true;
    src.connect(s.ringGain ?? ctx.destination);
    src.start();
    s.ringtoneSource = src;
  } catch {
    return false;
  }
  return true;
}

/**
 * One ring "tick": prefer the OS ringtone (a seamless loop — once its source
 * is running, later ticks are no-ops that merely keep retrying after an
 * autoplay-suspended start); fall back to the synthesized double-ring.
 */
function ringtoneTick(): void {
  const s = S();
  if (!s.ringtoneActive) return;
  if (s.systemRingtone && !s.systemRingtoneFailed) {
    void playSystemRingtone().then((ok) => {
      if (!ok) ringtoneBurst();
    });
  } else {
    ringtoneBurst();
  }
}

export function startRingtone(): void {
  const s = S();
  if (s.ringtoneTimer) return;
  s.ringtoneActive = true;
  setRingBus(true); // re-open the ring bus (a prior stop may have zeroed it)
  ringtoneTick();
  s.ringtoneTimer = setInterval(ringtoneTick, 2600);
}

export function stopRingtone(): void {
  const s = S();
  s.ringtoneActive = false;
  if (s.ringtoneTimer) {
    clearInterval(s.ringtoneTimer);
    s.ringtoneTimer = null;
  }
  if (s.ringtoneSource) {
    try { s.ringtoneSource.stop(); } catch { /* already stopped */ }
    try { s.ringtoneSource.disconnect(); } catch { /* noop */ }
    s.ringtoneSource = null;
  }
  setRingBus(false); // kill any already-scheduled ring oscillators INSTANTLY
}

/** Outgoing-call ringback: a soft, low dual-tone purr, repeating every 3 s. */
function ringbackBurst(): void {
  if (!S().ringbackActive) return;
  void playBurst([
    { frequency: 440, startOffset: 0.0, duration: 1.0, gain: 0.07, type: 'sine' },
    { frequency: 480, startOffset: 0.0, duration: 1.0, gain: 0.06, type: 'sine' },
  ], () => S().ringbackActive, 'ring');
}

export function startRingback(): void {
  const s = S();
  if (s.ringbackTimer) return;
  s.ringbackActive = true;
  setRingBus(true); // re-open the ring bus (a prior stop may have zeroed it)
  ringbackBurst();
  s.ringbackTimer = setInterval(ringbackBurst, 3000);
}

export function stopRingback(): void {
  const s = S();
  s.ringbackActive = false;
  if (s.ringbackTimer) {
    clearInterval(s.ringbackTimer);
    s.ringbackTimer = null;
  }
  setRingBus(false); // kill any already-scheduled ring oscillators INSTANTLY
}

/** Stop every looping call sound (safety net on teardown). */
export function stopAllCallSounds(): void {
  stopRingtone();
  stopRingback();
}

// ── One-shot effects ───────────────────────────────────────────────────────

/** Call connected — short rising two-note blip. */
export function playConnectTone(): void {
  void playBurst([
    { frequency: 540, startOffset: 0.0, duration: 0.1, gain: 0.12, type: 'sine' },
    { frequency: 760, startOffset: 0.08, duration: 0.14, gain: 0.12, type: 'sine' },
  ]);
}

/** Call ended — short descending two-note blip. */
export function playEndTone(): void {
  void playBurst([
    { frequency: 640, startOffset: 0.0, duration: 0.12, gain: 0.11, type: 'sine' },
    { frequency: 420, startOffset: 0.1, duration: 0.18, gain: 0.10, type: 'sine' },
  ]);
}
